










































import Vue, { VueConstructor } from 'vue'
import { mapState } from 'vuex'
import { EarTrainingSet, EarTrainingRestrictions, EarTrainingGenerator } from '@/application/models/earTrainingSet'
import { TRIAD } from '../application/models/chords'
import { octaves, defaultOctave } from '../application/models/octaves'
import { notes } from '../application/models/notes'
import { queueProgression } from '@/application/TonePlayer'
import ChordDisplay from '@/components/ChordDisplay.vue'
import { ScaleInKey, KeyScaleDegree } from '../application/models/ScaleInKey'
import * as Tone from 'tone'
import { ChordProgression, queryChordSeparater } from '../application/models/chordProgression'
import { majorScale } from '../application/models/scales'
import IconPlay from '@/assets/icons/icon-play.svg'
import IconPause from '@/assets/icons/icon-pause.svg'
import IconDoorExit from '@/assets/icons/icon-door-exit.svg'
import IconInboxUpload from '@/assets/icons/icon-inbox-upload.svg'
import IconDuplicate from '@/assets/icons/icon-duplicate.svg'
import IconEar from '@/assets/icons/icon-ear.svg'
import hasScaleInKey, { HasScaleInKeyMixinData } from '@/mixins/hasScaleInKey'
import { RawLocation } from 'vue-router'
import hasBreakpoints, { HasBreakpointsMixinComputed } from '@/mixins/hasBreakpoints'
import hasEnharmonics, { HasEnharmonicsMixinData } from '@/mixins/hasEnharmonics'

type Mixins = HasScaleInKeyMixinData & HasBreakpointsMixinComputed & HasEnharmonicsMixinData

export default (Vue as VueConstructor<Mixins>).extend({
  mixins: [hasScaleInKey, hasBreakpoints, hasEnharmonics],
  components: {
    ChordDisplay,
    IconPlay,
    IconPause,
    IconDoorExit,
    IconInboxUpload,
    IconDuplicate,
    IconEar
  },
  props: {
    degrees: { type: Array as () => number[] },
    variants: { type: Array as () => string[], default: (() => [TRIAD.toString()]) as () => string[] },
    inversions: { type: Boolean, default: true },
    octaves: { type: Array as () => number[], default: (() => octaves.slice(0, 2)) as () => number[] },
    canShare: { type: Boolean }
  },
  data() {
    return {
      playing: false,
      chordProgression: new ChordProgression([], new ScaleInKey(notes[0], majorScale)) // this is just here for the typing. It will get reassigned before it is used
    }
  },
  computed: {
    ...mapState('sampler', [
      'samplerLoaded',
      'sampler'
    ]),
    trainingSet(): EarTrainingSet {
      return new EarTrainingSet(this.degrees as number[])
    },
    restrictions(): EarTrainingRestrictions {
      return new EarTrainingRestrictions(this.variants as string[], this.inversions, this.octaves as number[])
    },
    scaleDegrees(): KeyScaleDegree[] {
      return this.trainingSet.degrees.map(degree => (this.scaleInKey as ScaleInKey).getDegree(degree, false))
    },
    buttonText(): string {
      return this.playing ? 'Stop' : 'Play'
    },
    progressionGenerator(): EarTrainingGenerator {
      return new EarTrainingGenerator(this.trainingSet, this.restrictions, this.scaleInKey)
    },
    disablePlayback(): boolean {
      return !this.samplerLoaded || this.chordProgression.isEmpty
    },
    createLink(): RawLocation {
      const degrees = this.chordProgression.chords.map(c => {
        return c ? `${c.degree}.3.0.${defaultOctave}` : 'r'
      })
      return {
        name: 'Create',
        query: {
          chords: degrees.join(queryChordSeparater)
        }
      }
    },
    keymap(): Keymap {
      return {
        'space': event => {
          event.preventDefault()
          this.toggle()
        }
      }
    }
  },
  watch: {
    progressionGenerator: {
      handler() {
        this.chordProgression = this.progressionGenerator.generateProgression()
      },
      immediate: true
    },
    chordProgression: {
      handler() {
        this.queueProgression()
      },
      immediate: true
    }
  },
  beforeMount() {
    Tone.Transport.loop = true
    Tone.Transport.bpm.value = 200
    Tone.Transport.stop()
    Tone.Transport.on('start', () => {this.playing = true})
    Tone.Transport.on('stop', () => {this.playing = false})
    Tone.Transport.on('pause', () => {this.playing = false})
  },
  methods: {
    async toggle() {
      await Tone.start()
      const state = Tone.Transport.state
      if(state === 'started') {
        Tone.Transport.stop()
        this.$gtag.event('training: stop playback')
      } else {
        Tone.Transport.start('+0.1')
        this.$gtag.event('training: start playback')
      }
    },
    queueProgression() {
      queueProgression(this.sampler, this.chordProgression, this.scaleInKey, false, () => {
        this.chordProgression = this.progressionGenerator.generateProgression()
      })
    }
  }
})
